import { useEffect, useState } from 'react';
import { useToast } from '../_common/Toast';
import { useQuery } from './useQuery';
import { useHistory, useLocation } from 'react-router-dom';
import { OrderJobTask } from '@/redux/slices/slice_types';
import { selectCurrentTask, selectTaskDrawerLoading } from '@/redux/selectors/taskDrawerSelectors';
import { getCurrentTask, setCurrentTask } from '@/redux/slices/taskDrawerSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

const useTaskDrawer = () => {
  const query = useQuery();
  const dispatch = useAppDispatch();
  const taskLoading = useAppSelector(selectTaskDrawerLoading);
  const activeTask = useAppSelector(selectCurrentTask);
  const id = query.get('id');
  const history = useHistory();
  const { search: filterParams, pathname } = useLocation();
  const { error } = useToast();
  const [isDirty, setIsDirty] = useState(false);

  const setActiveTask = (task: OrderJobTask | null) => dispatch(setCurrentTask(task));

  const fetchTask = async (id: string) => {
    try {
      await dispatch(getCurrentTask(id)).unwrap();
    } catch (ex: any) {
      error(ex.response || 'There was an error retrieving the task.');
    }
  };

  const onDrawerClose = () => {
    setActiveTask(null);
    setIsDirty(false);
    const searchParams = new URLSearchParams(filterParams);
    searchParams.delete('id');
    history.replace({ search: '?' + searchParams.toString() });
  };

  const onActiveTaskChange = (task: any) => {
    const searchParams = new URLSearchParams(filterParams);
    searchParams.set('id', task.id);
    history.replace({ search: '?' + searchParams.toString() });
    if (isDirty) {
      return false;
    }
    setActiveTask(task);
  };

  useEffect(() => {
    if (id) {
      fetchTask(id);
    }
  }, [id]);

  useEffect(() => {
    if (isDirty && activeTask?.id?.toString() !== id?.toString() && id) {
      fetchTask(id);
      setIsDirty(false);
    } else if (!id) {
      setActiveTask(null);
      setIsDirty(false);
    }
  }, [pathname, isDirty, id, activeTask]);

  return {
    activeTask,
    isDirty,
    fetchTask,
    onActiveTaskChange,
    onDrawerClose,
    setActiveTask,
    setIsDirty,
    taskLoading
  };
};

export default useTaskDrawer;
